import React, { useState } from 'react';
import { Card, Button, Form, Select, InputNumber, DatePicker, Table, Modal, message, Input, Space, Tooltip, Tag, Alert } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const ReadingsPage = () => {
  const { meters, subscribers, readings, addReading, updateReading } = useApp();
  const { user } = useAuth();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedReading, setSelectedReading] = useState(null);
  const isViewer = user?.role === 'viewer';

  const handleAddReading = () => {
    setIsFormOpen(true);
  };

  const handleReadingSubmit = async (values) => {
    try {
      setLoading(true);

      // Get the previous reading for this meter
      const meterReadings = readings.filter(r => r.meterId === values.meterId);
      const lastReading = meterReadings.length > 0
        ? Math.max(...meterReadings.map(r => r.meterReading))
        : 0;

      const newReading = values.meterReading;
      if (newReading <= lastReading) {
        message.error('La nouvelle lecture doit être supérieure à la précédente');
        setLoading(false);
        return;
      }

      const reading = {
        meterId: values.meterId,
        subscriberId: values.subscriberId,
        readingDate: values.readingDate.toISOString(),
        meterReading: values.meterReading,
        consumption: newReading - lastReading,
        notes: values.notes
      };

      setLoading(false);

      // Ask for confirmation before recording
      Modal.confirm({
        title: 'Confirmation d\'enregistrement',
        content: (
          <div>
            <p><strong>Attention:</strong> Une fois enregistré, la date du relevé ne peut pas être modifiée.</p>
            <p><strong>Détails du relevé:</strong></p>
            <ul>
              <li>Date: <strong>{values.readingDate.format('DD/MM/YYYY')}</strong></li>
              <li>Lecture: <strong>{values.meterReading} m³</strong></li>
              <li>Consommation: <strong>{newReading - lastReading} m³</strong></li>
            </ul>
            <p>Êtes-vous sûr de vouloir enregistrer ce relevé?</p>
          </div>
        ),
        okText: 'Confirmer',
        cancelText: 'Annuler',
        onOk() {
          // Record the reading
          addReading(reading, user?.id);
          message.success('Relevé enregistré');
          setIsFormOpen(false);
          form.resetFields();
        },
        onCancel() {
          // Do nothing
        }
      });
    } catch (error) {
      message.error('Erreur');
      setLoading(false);
    }
  };

  const handleEditReading = (reading) => {
    // Check if reading is initial and cannot be edited
    if (reading.isInitial) {
      message.error('Ce relevé initial ne peut pas être modifié');
      return;
    }

    // Check if reading has an invoice generated
    if (reading.invoiceGenerated || reading.linkedInvoiceId) {
      message.error('Ce relevé a déjà généré une facture et ne peut pas être modifié');
      return;
    }

    // Check if there's a more recent reading for the same meter
    const meterReadings = readings.filter(r => r.meterId === reading.meterId);
    const moreRecentReadings = meterReadings.filter(r => {
      const rDate = new Date(r.readingDate);
      const currentDate = new Date(reading.readingDate);
      return rDate > currentDate;
    });

    if (moreRecentReadings.length > 0) {
      message.error('Ce relevé ne peut pas être modifié car il existe un relevé plus récent pour ce compteur (incohérence chronologique)');
      return;
    }

    setSelectedReading(reading);
    editForm.setFieldsValue({
      meterId: reading.meterId,
      subscriberId: reading.subscriberId,
      readingDate: dayjs(reading.readingDate),
      meterReading: reading.meterReading,
      notes: reading.notes
    });
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      setLoading(true);

      // Vérifier que la date n'a pas changé vers une date antérieure
      const newDate = new Date(values.readingDate.toISOString());
      const currentDate = new Date(selectedReading.readingDate);
      
      if (newDate.getTime() !== currentDate.getTime()) {
        message.error('La date d\'un relevé ne peut pas être modifiée pour maintenir la cohérence chronologique');
        setLoading(false);
        return;
      }

      const meterReadings = readings.filter(r => r.meterId === values.meterId && r.id !== selectedReading.id);
      const lastReading = meterReadings.length > 0
        ? Math.max(...meterReadings.map(r => r.meterReading))
        : 0;

      const newReading = values.meterReading;
      if (newReading <= lastReading) {
        message.error('La nouvelle lecture doit être supérieure à la précédente');
        return;
      }

      const updatedReading = {
        ...selectedReading,
        meterId: values.meterId,
        subscriberId: values.subscriberId,
        readingDate: values.readingDate.toISOString(),
        meterReading: values.meterReading,
        consumption: newReading - lastReading,
        notes: values.notes
      };

      updateReading(selectedReading.id, updatedReading, user?.id);

      message.success('Relevé modifié');
      setIsEditOpen(false);
      editForm.resetFields();
      setSelectedReading(null);
    } catch (error) {
      message.error('Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'readingDate',
      key: 'readingDate',
      render: (date) => new Date(date).toLocaleDateString('fr-FR')
    },
    {
      title: 'Compteur',
      dataIndex: 'meterId',
      key: 'meterId'
    },
    {
      title: 'Libellé compteur',
      dataIndex: 'meterId',
      key: 'meterLabel',
      render: (meterId) => {
        const meter = meters.find(m => m.id === meterId);
        return meter ? `${meter.brand} ${meter.model}` : '-';
      }
    },
    {
      title: 'Lecture',
      dataIndex: 'meterReading',
      key: 'meterReading'
    },
    {
      title: 'Consommation (m³)',
      dataIndex: 'consumption',
      key: 'consumption'
    },
    {
      title: 'Statut',
      dataIndex: 'invoiceGenerated',
      key: 'invoiceGenerated',
      render: (invoiceGenerated, record) => (
        record.isInitial
          ? <Tag color="blue">Relevé initial</Tag>
          : invoiceGenerated 
          ? <Tag color="red">Facturé</Tag>
          : <Tag color="green">Modifiable</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => !isViewer && (
        <Tooltip title={
          record.isInitial 
            ? 'Relevé initial - non modifiable'
            : record.invoiceGenerated || record.linkedInvoiceId 
            ? 'Facture déjà générée' 
            : 'Modifier ce relevé'
        }>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditReading(record)}
            disabled={record.isInitial || record.invoiceGenerated || record.linkedInvoiceId}
          >
            Modifier
          </Button>
        </Tooltip>
      )
    }
  ];

  const handleMeterChange = (meterId) => {
    const meter = meters.find(m => m.id === meterId);
    if (meter) {
      form.setFieldValue('subscriberId', meter.subscriberId);
    }
  };

  // Get the last reading date for a meter
  const getLastReadingDate = (meterId) => {
    const meterReadings = readings.filter(r => r.meterId === meterId);
    if (meterReadings.length === 0) return null;
    
    const sortedReadings = meterReadings.sort((a, b) => 
      new Date(b.readingDate) - new Date(a.readingDate)
    );
    
    return new Date(sortedReadings[0].readingDate);
  };

  // Check if a reading already exists for this meter on this date
  const readingExistsOnDate = (meterId, date) => {
    const selectedDate = new Date(date.toDate());
    selectedDate.setHours(0, 0, 0, 0);

    return readings.some(r => {
      if (r.meterId !== meterId) return false;
      const readingDate = new Date(r.readingDate);
      readingDate.setHours(0, 0, 0, 0);
      return readingDate.getTime() === selectedDate.getTime();
    });
  };

  return (
    <div>
      <h1>Gestion des Relevés</h1>

      {isViewer && (
        <Alert
          message="Mode Consultation"
          description="Vous êtes en mode lecture seule. Les modifications ne sont pas autorisées pour votre rôle."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      {!isViewer && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddReading}
          style={{ marginBottom: 20 }}
        >
          Enregistrer un relevé
        </Button>
      )}

      <Card>
        <Table
          columns={columns}
          dataSource={readings.filter(r => !r.isInitial)}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="Enregistrer un relevé"
        open={isFormOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsFormOpen(false)}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleReadingSubmit}
        >
          <Form.Item
            label="Compteur"
            name="meterId"
            rules={[{ required: true, message: 'Le compteur est obligatoire' }]}
          >
            <Select
              placeholder="Sélectionner un compteur"
              options={meters.map(m => ({
                label: `${m.brand} ${m.model} (Série: ${m.serialNumber})`,
                value: m.id
              }))}
              showSearch
              onChange={handleMeterChange}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Abonné"
            name="subscriberId"
          >
            <Select
              placeholder="Abonné"
              disabled
              options={subscribers.map(s => ({
                label: `${s.firstName} ${s.lastName}`,
                value: s.id
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Date du relevé"
            name="readingDate"
            initialValue={dayjs()}
            rules={[
              { required: true, message: 'La date est obligatoire' },
              {
                validator: (_, value) => {
                  const meterId = form.getFieldValue('meterId');
                  if (!meterId || !value) return Promise.resolve();
                  
                  // Vérifier si un relevé existe déjà pour cette date
                  if (readingExistsOnDate(meterId, value)) {
                    return Promise.reject(
                      new Error('Un relevé existe déjà pour ce compteur à cette date')
                    );
                  }
                  
                  const lastReadingDate = getLastReadingDate(meterId);
                  if (!lastReadingDate) return Promise.resolve();
                  
                  // Comparer les dates (ignorer l'heure)
                  const selectedDate = new Date(value.toDate());
                  selectedDate.setHours(0, 0, 0, 0);
                  
                  const lastDate = new Date(lastReadingDate);
                  lastDate.setHours(0, 0, 0, 0);
                  
                  if (selectedDate < lastDate) {
                    return Promise.reject(
                      new Error(`La date doit être >= au dernier relevé (${lastReadingDate.toLocaleDateString('fr-FR')})`)
                    );
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Lecture du compteur (m³)"
            name="meterReading"
            rules={[{ required: true, message: 'La lecture est obligatoire' }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Notes"
            name="notes"
          >
            <Input.TextArea rows={2} placeholder="Notes additionnelles" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Modifier un relevé"
        open={isEditOpen}
        onOk={() => editForm.submit()}
        onCancel={() => setIsEditOpen(false)}
        okText="Mettre à jour"
        cancelText="Annuler"
        confirmLoading={loading}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            label="Compteur"
            name="meterId"
            rules={[{ required: true, message: 'Le compteur est obligatoire' }]}
          >
            <Select
              placeholder="Sélectionner un compteur"
              options={meters.map(m => ({
                label: `${m.brand} ${m.model} (Série: ${m.serialNumber})`,
                value: m.id
              }))}
              showSearch
              onChange={handleMeterChange}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Abonné"
            name="subscriberId"
          >
            <Select
              placeholder="Abonné"
              disabled
              options={subscribers.map(s => ({
                label: `${s.firstName} ${s.lastName}`,
                value: s.id
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Date du relevé"
            name="readingDate"
            rules={[
              { required: true, message: 'La date est obligatoire' },
              {
                validator: (_, value) => {
                  const meterId = editForm.getFieldValue('meterId');
                  if (!meterId || !value) return Promise.resolve();
                  
                  // Vérifier si un autre relevé existe déjà pour cette date
                  if (readingExistsOnDate(meterId, value) && selectedReading && new Date(selectedReading.readingDate).toDateString() !== value.toDate().toDateString()) {
                    return Promise.reject(
                      new Error('Un autre relevé existe déjà pour ce compteur à cette date')
                    );
                  }
                  
                  const lastReadingDate = getLastReadingDate(meterId);
                  if (!lastReadingDate) return Promise.resolve();
                  
                  // Comparer les dates (ignorer l'heure)
                  const selectedDate = new Date(value.toDate());
                  selectedDate.setHours(0, 0, 0, 0);
                  
                  const lastDate = new Date(lastReadingDate);
                  lastDate.setHours(0, 0, 0, 0);
                  
                  // Sauf si c'est le relevé en cours de modification
                  if (lastDate.getTime() === selectedReading?.id && selectedDate < lastDate) {
                    return Promise.reject(
                      new Error(`La date doit être >= au dernier relevé (${lastReadingDate.toLocaleDateString('fr-FR')})`)
                    );
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Lecture du compteur (m³)"
            name="meterReading"
            rules={[{ required: true, message: 'La lecture est obligatoire' }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Notes"
            name="notes"
          >
            <Input.TextArea rows={2} placeholder="Notes additionnelles" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReadingsPage;
